import { Head, Link } from "@inertiajs/react"
import Navbar from "@/components/chalky_booking/Navbar"
import Footer from "@/components/chalky_booking/Footer"

interface MentionsLegalesProps {
  brand: { line1: string; line2: string }
  nav: { services: string; agency: string; contact: string }
  footer: {
    description: string
    navigation: string
    contact: string
    copyright: string
    legal: string
    privacy: string
  }
  contact_email: string
  paths: { mentions_legales: string; confidentiality: string }
  locales: string[]
  current_locale: string
  language_labels: Record<string, string>
  last_updated: string
}

const SectionTitle = ({
  children,
  accent = "primary",
}: {
  children: React.ReactNode
  accent?: "primary" | "secondary"
}) => (
  <h2
    className={`text-2xl md:text-3xl font-bold text-contrast mb-6 pb-3 border-b-2 ${
      accent === "primary" ? "border-primary/20" : "border-secondary/20"
    }`}
  >
    {children}
  </h2>
)

export default function MentionsLegales({
  brand,
  nav,
  footer,
  contact_email,
  paths,
  locales,
  current_locale,
  language_labels,
  last_updated,
}: MentionsLegalesProps) {
  return (
    <>
      <Head>
        <title>Mentions légales — Chalky Booking</title>
        <meta name="description" content="Informations légales obligatoires concernant le site chalky-booking.fr et l'association CHALKY PRODUCTION." />
      </Head>

      <Navbar
        brand={brand}
        nav={nav}
        locales={locales}
        currentLocale={current_locale}
        languageLabels={language_labels}
      />

      <main className="min-h-screen bg-gradient-to-br from-light via-white to-light pb-12 pt-24">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-contrast mb-4">
              Mentions<span className="text-primary"> Légales</span>
            </h1>
            <p className="text-xl text-content max-w-2xl mx-auto">
              Informations légales obligatoires concernant le site chalky-booking.fr et l'association CHALKY PRODUCTION
            </p>
            <div className="text-sm text-content/70 mt-4">
              Dernière mise à jour : {last_updated}
            </div>
          </div>

          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
            <div className="mb-12">
              <SectionTitle>1. Éditeur du site</SectionTitle>
              <div className="bg-gradient-to-br from-primary/5 to-primary/10 p-8 rounded-xl border-l-4 border-primary">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-bold text-primary mb-3">Dénomination</h4>
                    <p className="text-content mb-4">CHALKY PRODUCTION</p>
                    <h4 className="font-bold text-primary mb-3">Forme juridique</h4>
                    <p className="text-content mb-4">Association loi 1901</p>
                    <h4 className="font-bold text-primary mb-3">Objet social</h4>
                    <p className="text-content">Organisation et production de concerts de musique, mise en place de tournées locales</p>
                  </div>
                  <div>
                    <h4 className="font-bold text-primary mb-3">Président</h4>
                    <p className="text-content mb-4">Florent Guilbaud</p>
                    <h4 className="font-bold text-primary mb-3">RNA</h4>
                    <p className="text-content mb-4">W442030572</p>
                    <h4 className="font-bold text-primary mb-3">Siège social</h4>
                    <div className="text-content">
                      <div>26 Bis rue Aristide Briand</div>
                      <div>44230 Saint-Sébastien-sur-Loire, France</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-12">
              <SectionTitle accent="secondary">2. Coordonnées</SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-secondary/10 to-secondary/20 p-6 rounded-xl text-center">
                  <div className="w-12 h-12 bg-secondary rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                    </svg>
                  </div>
                  <h4 className="font-bold text-secondary mb-2">E-mail</h4>
                  <p className="text-sm text-content/80">
                    <a href={`mailto:${contact_email}`} className="text-secondary hover:underline font-semibold">
                      {contact_email}
                    </a>
                  </p>
                </div>
                <div className="bg-gradient-to-br from-orange-500/10 to-orange-600/20 p-6 rounded-xl text-center">
                  <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                    </svg>
                  </div>
                  <h4 className="font-bold text-orange-600 mb-2">Adresse</h4>
                  <div className="text-sm text-content/80">
                    <div>26 Bis rue Aristide Briand</div>
                    <div>44230 Saint-Sébastien-sur-Loire</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-12">
              <SectionTitle>3. Hébergement</SectionTitle>
              <div className="bg-gradient-to-br from-blue-500/5 to-blue-600/10 p-8 rounded-xl border-l-4 border-blue-500">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-bold text-blue-600 mb-3">Hébergeur</h4>
                    <p className="text-content mb-4">Clever Cloud SAS</p>
                    <h4 className="font-bold text-blue-600 mb-3">Adresse</h4>
                    <div className="text-content mb-4">
                      <div>4 rue Voltaire</div>
                      <div>44000 Nantes, France</div>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-bold text-blue-600 mb-3">Site web</h4>
                    <p className="text-content mb-4">
                      <a href="https://www.clever-cloud.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-semibold">
                        www.clever-cloud.com
                      </a>
                    </p>
                    <h4 className="font-bold text-blue-600 mb-3">SIRET</h4>
                    <p className="text-content">524 172 699 00034</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-12">
              <SectionTitle accent="secondary">4. Directeur de publication</SectionTitle>
              <div className="prose max-w-none text-content leading-relaxed">
                <p className="mb-4">Le directeur de la publication du site chalky-booking.fr est :</p>
                <div className="bg-gradient-to-br from-secondary/10 to-secondary/20 p-6 rounded-xl text-center max-w-md mx-auto">
                  <div className="w-16 h-16 bg-secondary rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17.982 18.725A7.488 7.488 0 0012 15.75a7.488 7.488 0 00-5.982 2.975m11.963 0a9 9 0 10-11.963 0m11.963 0A8.966 8.966 0 0112 21a8.966 8.966 0 01-5.982-2.275M15 9.75a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <h4 className="font-bold text-secondary text-xl mb-2">Florent Guilbaud</h4>
                  <p className="text-content/80">Président de CHALKY PRODUCTION</p>
                  <p className="text-sm text-content/60 mt-2">Responsable des contenus publiés</p>
                </div>
              </div>
            </div>

            <div className="mb-12">
              <SectionTitle>5. Propriété intellectuelle</SectionTitle>
              <div className="prose max-w-none text-content leading-relaxed">
                <p className="mb-6">
                  L'ensemble du contenu de ce site (textes, images, vidéos, logos, icônes, sons, etc.) est protégé par les droits de propriété intellectuelle.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gradient-to-br from-red-500/10 to-red-600/20 p-6 rounded-xl">
                    <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center mb-4">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L5.636 5.636" />
                      </svg>
                    </div>
                    <h4 className="font-bold text-red-600 mb-3">Reproduction interdite</h4>
                    <p className="text-sm text-content/80">Toute reproduction totale ou partielle sans autorisation est strictement interdite</p>
                  </div>
                  <div className="bg-gradient-to-br from-green-500/10 to-green-600/20 p-6 rounded-xl">
                    <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mb-4">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h4 className="font-bold text-green-600 mb-3">Usage personnel autorisé</h4>
                    <p className="text-sm text-content/80">Consultation et copie privée pour usage personnel uniquement</p>
                  </div>
                </div>
                <div className="mt-6 bg-orange-500/10 p-6 rounded-xl border-l-4 border-orange-500">
                  <p className="font-semibold text-orange-600 mb-2">Droits d'auteur</p>
                  <p className="text-content/90">
                    Tous les éléments de ce site sont la propriété exclusive de CHALKY PRODUCTION ou font l'objet d'une autorisation d'utilisation.
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-12">
              <SectionTitle accent="secondary">6. Responsabilité et disclaimer</SectionTitle>
              <div className="prose max-w-none text-content leading-relaxed">
                <p className="mb-6">
                  CHALKY PRODUCTION s'efforce d'assurer l'exactitude et la mise à jour des informations diffusées sur ce site. Cependant :
                </p>
                <ul className="list-disc pl-6 space-y-3 mb-6">
                  <li>Les informations sont données à titre indicatif et peuvent évoluer</li>
                  <li>CHALKY PRODUCTION ne peut garantir l'absence d'erreurs ou d'omissions</li>
                  <li>L'utilisation des informations se fait sous votre responsabilité</li>
                  <li>CHALKY PRODUCTION ne peut être tenu responsable des dommages indirects</li>
                </ul>
                <div className="bg-gradient-to-br from-yellow-500/10 to-yellow-600/20 p-6 rounded-xl border-l-4 border-yellow-500 mt-6">
                  <div className="flex items-start gap-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                    </svg>
                    <div>
                      <p className="font-semibold text-yellow-600 mb-2">Clause de non-responsabilité</p>
                      <p className="text-content/90 text-sm">
                        L'utilisateur reconnaît utiliser ces informations sous sa propre responsabilité et dégage CHALKY PRODUCTION de toute responsabilité à ce titre.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-12">
              <SectionTitle>7. Liens hypertextes</SectionTitle>
              <div className="prose max-w-none text-content leading-relaxed">
                <p className="mb-6">
                  Ce site peut contenir des liens vers d'autres sites web. CHALKY PRODUCTION n'exerce aucun contrôle sur ces sites et décline toute responsabilité quant à leur contenu.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/20 p-6 rounded-xl">
                    <h4 className="font-bold text-blue-600 mb-3">Liens sortants</h4>
                    <p className="text-sm text-content/80">Les liens vers des sites externes sont fournis à titre informatif uniquement</p>
                  </div>
                  <div className="bg-gradient-to-br from-purple-500/10 to-purple-600/20 p-6 rounded-xl">
                    <h4 className="font-bold text-purple-600 mb-3">Demande de lien</h4>
                    <p className="text-sm text-content/80">Tout lien vers notre site doit faire l'objet d'une autorisation préalable</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-12">
              <SectionTitle accent="secondary">8. Cookies et collecte de données</SectionTitle>
              <div className="prose max-w-none text-content leading-relaxed">
                <p className="mb-6">
                  Ce site peut utiliser des cookies pour améliorer l'expérience utilisateur et réaliser des statistiques de visite.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-gradient-to-br from-green-500/10 to-green-600/20 p-6 rounded-xl text-center">
                    <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <h4 className="font-bold text-green-600 mb-2">Cookies techniques</h4>
                    <p className="text-sm text-content/80">Nécessaires au fonctionnement du site</p>
                  </div>
                  <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/20 p-6 rounded-xl text-center">
                    <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
                      </svg>
                    </div>
                    <h4 className="font-bold text-blue-600 mb-2">Analytics</h4>
                    <p className="text-sm text-content/80">Statistiques anonymisées de fréquentation</p>
                  </div>
                  <div className="bg-gradient-to-br from-purple-500/10 to-purple-600/20 p-6 rounded-xl text-center">
                    <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 13.5V3.75m0 9.75a1.5 1.5 0 010 3m0-3a1.5 1.5 0 000 3m0 3.75V16.5m12-3V3.75m0 9.75a1.5 1.5 0 010 3m0-3a1.5 1.5 0 000 3m0 3.75V16.5m-6-9V3.75m0 3.75a1.5 1.5 0 010 3m0-3a1.5 1.5 0 000 3m0 3.75V10.5" />
                      </svg>
                    </div>
                    <h4 className="font-bold text-purple-600 mb-2">Contrôle utilisateur</h4>
                    <p className="text-sm text-content/80">Possibilité de désactiver les cookies</p>
                  </div>
                </div>
                <div className="mt-6 bg-primary/10 p-6 rounded-xl border-l-4 border-primary">
                  <p className="font-semibold text-primary mb-2">Information cookies</p>
                  <p className="text-content/90 mb-2">
                    Pour plus d'informations sur notre utilisation des cookies et la gestion de vos données personnelles, consultez notre
                  </p>
                  <p>
                    <Link href={paths.confidentiality} className="text-primary font-semibold hover:underline">
                      Politique de confidentialité
                    </Link>
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <SectionTitle>9. Droit applicable et juridiction</SectionTitle>
              <div className="prose max-w-none text-content leading-relaxed">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="font-bold text-contrast mb-4">Droit applicable</h4>
                    <div className="bg-gradient-to-br from-red-500/10 to-red-600/20 p-6 rounded-xl">
                      <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center mb-4">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15l-.75 18h-13.5L4.5 3z" />
                        </svg>
                      </div>
                      <p className="text-content/90">Les présentes mentions légales sont soumises au droit français.</p>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-bold text-contrast mb-4">Juridiction compétente</h4>
                    <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/20 p-6 rounded-xl">
                      <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mb-4">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 21v-8.25M15.75 21v-8.25M8.25 21v-8.25M3 9l9-6 9 6m-1.5 12V10.332A48.36 48.36 0 0012 9.75c-2.551 0-5.056.2-7.5.582V21M3 21h18M12 6.75h.008v.008H12V6.75z" />
                        </svg>
                      </div>
                      <p className="text-content/90">En cas de litige, les tribunaux de Nantes seront seuls compétents.</p>
                    </div>
                  </div>
                </div>
                <div className="mt-8 bg-yellow-500/10 p-6 rounded-xl border-l-4 border-yellow-500">
                  <p className="font-semibold text-yellow-600 mb-2">Règlement des différends</p>
                  <p className="text-content/90">
                    En cas de différend, nous privilégions la résolution amiable. À défaut d'accord, le litige sera porté devant les tribunaux compétents de Nantes.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-16">
            <div className="bg-contrast p-8 rounded-3xl text-white">
              <h3 className="text-2xl font-bold mb-4">Questions sur nos mentions légales ?</h3>
              <p className="text-white/90 mb-6">Notre équipe est disponible pour répondre à vos questions</p>
              <div className="flex gap-4 justify-center flex-wrap">
                <a
                  href={`mailto:${contact_email}`}
                  className="inline-block px-8 py-4 bg-white text-contrast font-bold rounded-full hover:shadow-2xl hover:scale-105 transition-all duration-300"
                >
                  Nous contacter
                </a>
                <Link
                  href={paths.confidentiality}
                  className="inline-block px-8 py-4 bg-transparent text-white font-bold rounded-full border-2 border-white hover:bg-white hover:text-contrast transition-all duration-300"
                >
                  Confidentialité
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer
        brand={brand}
        nav={nav}
        footer={footer}
        contactEmail={contact_email}
        paths={{
          mentionsLegales: paths.mentions_legales,
          confidentiality: paths.confidentiality,
        }}
      />
    </>
  )
}
